import { useEffect, useRef, useState } from "react";
import ChevronIcon from "assets/icons/ChevronIcon";
import classNames from "classnames";
import { State } from "redux/store";
import { connect, useDispatch } from "react-redux";
import { useParams } from "react-router";
import { setSelectedCarouselIndex } from "redux/Modals/actions";
import { getSelectedCarouselIndex } from "redux/Modals/selectors";

import styles from "./style.module.scss";
import { CarouselTitle } from "../CarouselTitle/CarouselTitle";

const ImageCarousel = ({
  isExpanded,
  setIsExpanded,
  navigationCluster,
  imageIndex,
  isContentLoading,
  timestamp,
  onImageChange,
}: any) => {
  const [hoverId, setHoverId] = useState<null | number>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { imageId } = useParams();

  const previews = navigationCluster.previews;

  useEffect(() => {
    if (imageIndex !== 0 || !imageId || previews.length === 0) return;

    const foundIndex = previews.findIndex((p: any) => {
      const id = p.type === "IMAGE" ? p.media_id : p.image_id;
      return id === imageId;
    });

    if (foundIndex > 0) {
      dispatch(setSelectedCarouselIndex(foundIndex));
    }
  }, [imageIndex, imageId, previews, dispatch]);

  const isLastIndex = imageIndex === previews.length - 1;
  const isFirstIndex = imageIndex === 0;
  const additionalClusterType = navigationCluster.type
    ? navigationCluster.type.charAt(0).toUpperCase() +
      navigationCluster.type.slice(1).toLowerCase()
    : "Images";

  const leftScrollClassnames = classNames(styles.leftScroll, {
    [styles.disabledScroll]: isFirstIndex || isContentLoading,
  });

  const rightcrollClassnames = classNames(styles.rightScroll, {
    [styles.disabledScroll]: isLastIndex || isContentLoading,
  });

  const handleImageChange = (index: number) => {
    onImageChange(previews[index]);
  };

  const selectNextImage = () => {
    handleImageChange(imageIndex + 1);
    dispatch(setSelectedCarouselIndex(imageIndex + 1));
  };

  const selectPreviousImage = () => {
    handleImageChange(imageIndex - 1);
    dispatch(setSelectedCarouselIndex(imageIndex - 1));
  };

  const handleImageSelect = (index: number) => {
    if (index === imageIndex) return;
    dispatch(setSelectedCarouselIndex(index));
    handleImageChange(index);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowRight" && !isLastIndex) {
      selectNextImage();
    } else if (e.key === "ArrowLeft" && !isFirstIndex) {
      selectPreviousImage();
    }
  };

  useEffect(() => {
    if (!isContentLoading && isExpanded) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => window.removeEventListener("keydown", handleKeyDown);
    //eslint-disable-next-line
  }, [imageIndex, isExpanded, isContentLoading]);

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.carouselHeader}>
        <div
          className={styles.carouselTitleBox}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <CarouselTitle
            text={`Similar ${additionalClusterType}`}
            count={previews.length}
            tooltipText={`Additional Cluster ${additionalClusterType}`}
          />
        </div>
        <div
          className={classNames(styles.actionButtons, {
            [styles.expanded]: isExpanded,
          })}
        >
          {isExpanded && (
            <div className={styles.scrollButtons}>
              <div
                onClick={selectPreviousImage}
                className={leftScrollClassnames}
              >
                <ChevronIcon direction="left" />
              </div>
              <div onClick={selectNextImage} className={rightcrollClassnames}>
                <ChevronIcon direction="right" />
              </div>
            </div>
          )}
          <div className={styles.dropDownIcon} onClick={setIsExpanded}>
            {isExpanded ? (
              <ChevronIcon direction="up" />
            ) : (
              <ChevronIcon direction="down" />
            )}
          </div>
        </div>
      </div>
      <div
        className={styles.carouselBody}
        style={isExpanded ? undefined : { height: 0 }}
      >
        <div className={styles.carouselImages}>
          {previews.map((image: any, index: number) => {
            const carouselImageSource =
              image.media_thumb_uri || image.media_uri;

            return (
              <div
                className={styles.carouselImageContainer}
                ref={index === imageIndex ? containerRef : undefined}
                key={index}
                onMouseEnter={() => {
                  setHoverId(index);
                }}
                onMouseLeave={() => setHoverId(null)}
                onClick={() => handleImageSelect(index)}
              >
                <img
                  className={styles.carouselImage}
                  src={carouselImageSource}
                  alt={image.file_name}
                  loading="lazy"
                />
                {(index === hoverId || index === imageIndex) && (
                  <div className={styles.hoveredImage}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    imageIndex: getSelectedCarouselIndex(state),
  };
};
export default connect(mapStateToProps)(ImageCarousel);
